import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { DataTable } from '@/v2/components/DataTable/tasks/components/data-table';
import { DataTableColumnHeader } from '@/v2/components/DataTable/tasks/components/data-table-column-header';
import { format } from 'date-fns';
import useFetch from 'use-http';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import {
  useUserDispatch,
  useUserState,
} from '@/components/context/UserContext';
import { getQueryString } from '@/components/constants/visitsQueryStringBuilder';
import { InfoButton } from '@/components/ui/shared/InfoButton';

export default function InstrumentalResearchesTable() {
  const { t } = useTranslation();
  const history = useHistory();
  const sessionDispatch = useUserDispatch();
  const userState = useUserState();

  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [filters, setFilters] = useState([]);
  const [selectedFilter, setSelectedFilter] = useState(null);
  const [search, setSearch] = useState('');
  const [dateRange, setDateRange] = useState(userState.dateFilter);

  const { get: getResearches, response: getResearchesResponse } = useFetch(
    '/UserAccount/InstrumentalResearchesList',
  );
  const { get: getResearch } = useFetch('/UserAccount/InstrumentalResearch');

  const fetchData = async (
    date = dateRange,
    searchTerm = search,
    filter = selectedFilter,
  ) => {
    setIsLoading(true);
    sessionDispatch({
      type: 'UPDATE',
      data: { dateFilter: date },
    });

    const query = getQueryString(
      date,
      searchTerm,
      null,
      filter,
      userState.espbiEsi,
    );
    const result = await getResearches(query);

    if (getResearchesResponse.ok) {
      const valid = result?.researches ?? [];
      const sorted = _.orderBy(valid, ['orderDate'], ['desc']);
      setData(sorted);

      const options =
        result?.filters?.map((value) => ({
          label: value,
          value,
        })) ?? [];
      setFilters(options);
    }
    setIsLoading(false);
  };

  const handleSelection = async (obj) => {
    setIsLoading(true);
    const query = userState.espbiEsi
      ? `?id=${obj.id}&espbiEsi=${userState.espbiEsi}`
      : `?id=${obj.id}`;
    const res = await getResearch(query);
    history.push('/userAccount/instrumental-researches/details', { data: res });
    setIsLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, [dateRange, userState?.espbiEsi]);

  return (
    <DataTable
      title="Instrumentiniai tyrimai"
      loading={isLoading}
      columns={columns({ handleSelection })}
      data={data}
      filterableColumns={['name', 'statusDisplay', 'orderDate']}
      onRowClick={(row) => handleSelection(row)}
    />
  );
}
const capitalizeFirstLetter = (text) => {
  if (!text) return '';
  return text.charAt(0).toUpperCase() + text.slice(1);
};
const columns = ({ handleSelection }) => [
  {
    accessorKey: 'name',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="TYRIMAS" />
    ),
    cell: ({ row }) => (
      <span className="font-medium max-w-[400px] truncate ">
        {capitalizeFirstLetter(row.original.name)}
      </span>
    ),
  },
  {
    accessorKey: 'orderDate',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="DATA" />
    ),
    cell: ({ row }) => {
      const rawDate = new Date(row.original.orderDate);
      const isMidnight =
        rawDate.getUTCHours() === 0 && rawDate.getUTCMinutes() === 0;
      return (
        <span className="text-muted-foreground">
          {isMidnight
            ? format(rawDate, 'yyyy-MM-dd')
            : format(rawDate, 'yyyy-MM-dd HH:mm')}
        </span>
      );
    },
  },
  {
    accessorKey: 'statusDisplay',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="BŪSENA" />
    ),
    cell: ({ row }) => (
      <Badge variant="neutral">{row.original.statusDisplay}</Badge>
    ),
  },
  {
    id: 'actions',
    header: '',
    cell: ({ row }) => (
      <div className="flex justify-end">
        <InfoButton
          onClick={(e) => {
            e.stopPropagation();
            handleSelection(row.original);
          }}
        >
          Informacija
        </InfoButton>
      </div>
    ),
  },
];
